import axios, { AxiosInstance } from 'axios';

import { attachSessionRefreshInterceptors } from 'services/sessionRefreshInterceptor';
import { getAccessToken } from 'utils/sessionTokens';

import { ActivityEvent, HubTask, TaskComment, TaskWorkspace, WorkspaceAnswers } from './types';

const baseUrl = () => (process.env.REACT_APP_MAESTRO_API_URL || '').replace(/\/$/, '');

let client: AxiosInstance | null = null;

const http = (): AxiosInstance => {
  const baseURL = baseUrl();
  if (!baseURL) {
    throw new Error('REACT_APP_MAESTRO_API_URL is not set.');
  }
  if (!client) {
    client = axios.create({ baseURL, timeout: 60_000 });
    client.interceptors.request.use((config) => {
      const token = getAccessToken();
      if (token && config.headers && !config.headers.authorization) {
        config.headers.authorization = token;
      }
      return config;
    });
    attachSessionRefreshInterceptors(client);
  }
  return client;
};

export const maestroErrorMessage = (error: unknown, fallback: string): string => {
  const data = (error as { response?: { data?: { detail?: unknown; messages?: string[] } } })
    ?.response?.data;
  if (data?.messages?.length) return data.messages.join(' ');
  if (typeof data?.detail === 'string' && data.detail) return data.detail;
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

export const listTasks = async (): Promise<HubTask[]> => {
  const response = await http().get<{ tasks: HubTask[] }>('/tasks');
  return response.data.tasks ?? [];
};

/** The task itself: spec, status, and saved answers. No plan numbers. */
export const getTask = async (taskId: string): Promise<TaskWorkspace> => {
  const response = await http().get<TaskWorkspace>(`/task/${taskId}`);
  return response.data;
};

/** Plan numbers. Live tasks wait on the calc engine. Submitted tasks read the freeze. */
export const getTaskData = async (
  taskId: string,
): Promise<{ render: TaskWorkspace['render']; view: NonNullable<TaskWorkspace['view']> }> => {
  const response = await http().get<{
    render: TaskWorkspace['render'];
    view: NonNullable<TaskWorkspace['view']>;
  }>(`/task/${taskId}/data_values`);
  return response.data;
};

export const saveInputs = async (
  taskId: string,
  edits: Array<{
    indicator_or_column: string;
    coordinates: Record<string, string>;
    value: number | string | null;
    block_id: number | null;
  }>,
): Promise<TaskWorkspace> => {
  const response = await http().put<TaskWorkspace>(`/task/${taskId}/inputs`, { edits });
  return response.data;
};

export const saveWorkspaceState = async (
  taskId: string,
  body: WorkspaceAnswers,
): Promise<WorkspaceAnswers> => {
  const response = await http().put<WorkspaceAnswers>(`/task/${taskId}/workspace-state`, body);
  return response.data;
};

export const submitTask = async (taskId: string): Promise<void> => {
  await http().post(`/task/${taskId}/submit`);
};

export const listComments = async (taskId: string): Promise<TaskComment[]> => {
  const response = await http().get<{ comments: TaskComment[] }>(`/task/${taskId}/comments`);
  return response.data.comments ?? [];
};

export const addComment = async (taskId: string, body: string): Promise<TaskComment> => {
  const response = await http().post<TaskComment>(`/task/${taskId}/comments`, { body });
  return response.data;
};

export const listActivity = async (taskId: string): Promise<ActivityEvent[]> => {
  const response = await http().get<{ events: ActivityEvent[] }>(`/task/${taskId}/activity`);
  return response.data.events ?? [];
};

export const reassignTask = async (taskId: string, assigneeUserIds: number[]): Promise<void> => {
  await http().put(`/task/${taskId}/assignees`, { assignee_user_ids: assigneeUserIds });
};

export const approveTask = async (taskId: string, note: string): Promise<TaskWorkspace> => {
  const response = await http().post<TaskWorkspace>(`/task/${taskId}/approve`, {
    note: note.trim() || null,
  });
  return response.data;
};

export const reopenTask = async (taskId: string, note: string): Promise<TaskWorkspace> => {
  const response = await http().post<TaskWorkspace>(`/task/${taskId}/reopen`, { note });
  return response.data;
};

export const closeTask = async (taskId: string): Promise<TaskWorkspace> => {
  const response = await http().post<TaskWorkspace>(`/task/${taskId}/close`);
  return response.data;
};

export const changeTaskDue = async (taskId: string, dueAt: string): Promise<void> => {
  await http().put(`/task/${taskId}/due`, { due_at: dueAt });
};
